import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Chip,
  Button,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { EnhancedProjectFile } from '../../types/file.types';
import { formatFileSize } from '../../types/file.types';

interface FileDetailPanelProps {
  file: EnhancedProjectFile | null;
  open: boolean;
  onClose: () => void;
  onUpdate: (fileId: string, data: FileUpdateData) => Promise<void>;
  onDownload: (file: EnhancedProjectFile) => void;
  onDelete: (fileId: string) => Promise<void>;
  onToggleFavorite: (fileId: string, isFavorite: boolean) => Promise<void>;
}

interface FileUpdateData {
  fileName?: string;
  description?: string;
  tags?: string[];
  importance?: 'low' | 'medium' | 'high';
}

const FileDetailPanel: React.FC<FileDetailPanelProps> = ({
  file,
  open,
  onClose,
  onUpdate,
  onDownload,
  onDelete,
  onToggleFavorite,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 편집 상태
  const [editedFileName, setEditedFileName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedTags, setEditedTags] = useState<string[]>([]);
  const [editedImportance, setEditedImportance] = useState<'low' | 'medium' | 'high'>('medium');
  const [newTag, setNewTag] = useState('');

  // 파일 변경 시 편집 상태 초기화
  useEffect(() => {
    if (file) {
      setEditedFileName(file.fileName || '');
      setEditedDescription(file.description || '');
      setEditedTags(file.tags || []);
      setEditedImportance(file.importance || 'medium');
      setIsEditing(false);
      setError(null);
    }
  }, [file]);

  // 편집 모드 시작
  const handleStartEdit = () => {
    setIsEditing(true);
    setError(null);
  };

  // 편집 취소
  const handleCancelEdit = () => {
    if (file) {
      setEditedFileName(file.fileName || '');
      setEditedDescription(file.description || '');
      setEditedTags(file.tags || []);
      setEditedImportance(file.importance || 'medium');
    }
    setIsEditing(false);
    setError(null);
  };

  // 저장
  const handleSave = async () => {
    if (!file) return;

    try {
      setLoading(true);
      setError(null);

      const updateData: FileUpdateData = {};
      
      // 변경된 필드만 업데이트
      if (editedFileName !== file.fileName) {
        updateData.fileName = editedFileName;
      }
      if (editedDescription !== (file.description || '')) {
        updateData.description = editedDescription;
      }
      if (JSON.stringify(editedTags) !== JSON.stringify(file.tags || [])) {
        updateData.tags = editedTags;
      }
      if (editedImportance !== (file.importance || 'medium')) {
        updateData.importance = editedImportance;
      }

      // 변경사항이 있을 때만 업데이트
      if (Object.keys(updateData).length > 0) {
        await onUpdate(file.id!, updateData);
      }

      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 태그 추가
  const handleAddTag = () => {
    if (newTag.trim() && !editedTags.includes(newTag.trim())) {
      setEditedTags([...editedTags, newTag.trim()]);
      setNewTag('');
    }
  };

  // 태그 제거
  const handleRemoveTag = (tagToRemove: string) => {
    setEditedTags(editedTags.filter(tag => tag !== tagToRemove));
  };

  // 삭제 확인
  const handleDelete = async () => {
    if (!file) return;
    
    if (window.confirm(`"${file.fileName}" 파일을 삭제하시겠습니까?`)) {
      try {
        setLoading(true);
        await onDelete(file.id!);
        onClose();
      } catch (err: any) {
        setError(err.message || '삭제 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    }
  };

  // 즐겨찾기 토글
  const handleToggleFavorite = async () => {
    if (!file) return;
    
    try {
      setLoading(true);
      await onToggleFavorite(file.id!, !file.isFavorite);
    } catch (err: any) {
      setError(err.message || '즐겨찾기 변경 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 중요도 색상
  const getImportanceColor = (importance: 'low' | 'medium' | 'high') => {
    switch (importance) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  if (!file) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400 },
          p: 2,
        },
      }}
    >
      {/* 헤더 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          파일 상세
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* 에러 메시지 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 로딩 */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {/* 썸네일 미리보기 */}
      {file.hasThumbnail && file.thumbnailUrl && (
        <Box
          sx={{
            width: '100%',
            height: 200,
            mb: 2,
            backgroundColor: 'grey.100',
            borderRadius: 1,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={file.thumbnailUrl}
            alt={file.fileName}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        </Box>
      )}

      {/* 파일명 */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          파일명
        </Typography>
        {isEditing ? (
          <TextField
            fullWidth
            size="small"
            value={editedFileName}
            onChange={(e) => setEditedFileName(e.target.value)}
            disabled={loading}
          />
        ) : (
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {file.fileName}
          </Typography>
        )}
      </Box>

      {/* 기본 정보 */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          기본 정보
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">크기:</Typography>
            <Typography variant="body2">{formatFileSize(file.fileSize)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">카테고리:</Typography>
            <Typography variant="body2">{file.category}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">업로더:</Typography>
            <Typography variant="body2">{file.uploadedByName || '알 수 없음'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">업로드:</Typography>
            <Typography variant="body2">
              {file.uploadedAt?.toDate().toLocaleDateString('ko-KR')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* 중요도 */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          중요도
        </Typography>
        {isEditing ? (
          <FormControl fullWidth size="small">
            <Select
              value={editedImportance}
              onChange={(e) => setEditedImportance(e.target.value as 'low' | 'medium' | 'high')}
              disabled={loading}
            >
              <MenuItem value="low">낮음</MenuItem>
              <MenuItem value="medium">보통</MenuItem>
              <MenuItem value="high">높음</MenuItem>
            </Select>
          </FormControl>
        ) : (
          <Chip
            label={file.importance === 'high' ? '높음' : file.importance === 'medium' ? '보통' : '낮음'}
            size="small"
            color={getImportanceColor(file.importance || 'medium')}
          />
        )}
      </Box>

      {/* 설명 */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          설명
        </Typography>
        {isEditing ? (
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            value={editedDescription}
            onChange={(e) => setEditedDescription(e.target.value)}
            placeholder="파일 설명을 입력하세요"
            disabled={loading}
          />
        ) : (
          <Typography variant="body2" color={file.description ? 'text.primary' : 'text.secondary'}>
            {file.description || '설명이 없습니다'}
          </Typography>
        )}
      </Box>

      {/* 태그 */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          태그
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: isEditing ? 1 : 0 }}>
          {editedTags.length > 0 ? (
            editedTags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                onDelete={isEditing ? () => handleRemoveTag(tag) : undefined}
                disabled={loading}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              태그가 없습니다
            </Typography>
          )}
        </Box>
        {isEditing && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              placeholder="새 태그"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              disabled={loading}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddTag}
              disabled={loading || !newTag.trim()}
            >
              추가
            </Button>
          </Box>
        )}
      </Box>

      {/* 통계 */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          통계
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">조회수:</Typography>
            <Typography variant="body2">{file.viewCount || 0}회</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">다운로드:</Typography>
            <Typography variant="body2">{file.downloadCount || 0}회</Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* 액션 버튼 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {isEditing ? (
          <>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={loading}
              fullWidth
            >
              저장
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancelEdit}
              disabled={loading}
              fullWidth
            >
              취소
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleStartEdit}
              disabled={loading}
              fullWidth
            >
              편집
            </Button>
            <Button
              variant="outlined"
              startIcon={file.isFavorite ? <StarIcon /> : <StarBorderIcon />}
              onClick={handleToggleFavorite}
              disabled={loading}
              fullWidth
              color={file.isFavorite ? 'warning' : 'inherit'}
            >
              {file.isFavorite ? '즐겨찾기 해제' : '즐겨찾기'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => onDownload(file)}
              disabled={loading}
              fullWidth
            >
              다운로드
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={loading}
              fullWidth
            >
              삭제
            </Button>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default FileDetailPanel;
